/**
 * useOrgMemberDetail — TanStack Query hook over
 * `GET /organizations/:slug/users/:userId`.
 *
 * Powers `/users/:userId` for any org member (not just self). The hook
 * captures the strong-ETag header alongside the response payload so the
 * companion `useUpdateOrgMemberProfile` mutation can thread it back as
 * `If-Match` without a separate state plumb-through.
 *
 * Status surface (closed):
 *   - 'loading'  — request in flight, or `useCurrentOrg` / `userId`
 *                  not yet ready.
 *   - 'ready'    — 200 OK; `data` + `etag` populated.
 *   - 'forbidden' — 403; caller-permission was revoked between page
 *                  mount and BE call.
 *   - 'notFound' — 404; the BE returns 404 for wrong-org, wrong-user,
 *                  and soft-deleted users alike (anti-enum).
 *   - 'error'    — anything else.
 *
 * ETag handling: the BE issues the canonical strong tag `"<version>"`
 * (just the integer, double-quoted — see `apps/api/src/modules/identity/
 * interface/utils/parse-if-match.ts`; the controller docstring's `"v5"`
 * example is misleading and the openapi description likewise — the
 * implementation expects a bare-integer strong tag). If the header is
 * stripped by an intermediary, the hook falls back to synthesising the
 * tag from `data.version` (always present on the BE response) so the
 * caller is never stuck in an unsaveable state.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { OrgMemberDetailView } from './types.js';

const FORBIDDEN = Symbol('org-member-detail:forbidden');
const NOT_FOUND = Symbol('org-member-detail:notFound');

export type UseOrgMemberDetailResult =
  | { readonly status: 'loading'; readonly refetch: () => void }
  | {
      readonly status: 'ready';
      readonly data: OrgMemberDetailView;
      readonly etag: string;
      readonly refetch: () => void;
    }
  | { readonly status: 'forbidden'; readonly refetch: () => void }
  | { readonly status: 'notFound'; readonly refetch: () => void }
  | { readonly status: 'error'; readonly refetch: () => void };

interface DetailDto {
  readonly id: string;
  readonly email: string;
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly emailVerifiedAt?: string | null;
  readonly status: 'active' | 'pending_verification' | 'suspended';
  readonly roles: ReadonlyArray<{ slug: string; displayName: string }>;
  readonly joinedAt: string;
  readonly lastSeenAt?: string | null;
  readonly jobTitle?: string | null;
  readonly department?: string | null;
  readonly phone?: string | null;
  readonly timeZone: string;
  readonly language: string;
  readonly country?: string | null;
  readonly managerId?: string | null;
  readonly version: number;
}

interface ReadyEnvelope {
  readonly data: OrgMemberDetailView;
  readonly etag: string;
}

function projectDetail(dto: DetailDto): OrgMemberDetailView {
  return {
    userId: dto.id,
    email: dto.email,
    firstName: dto.firstName ?? null,
    lastName: dto.lastName ?? null,
    emailVerifiedAt: dto.emailVerifiedAt ?? null,
    status: dto.status,
    roles: dto.roles,
    joinedAt: dto.joinedAt,
    lastSeenAt: dto.lastSeenAt ?? null,
    jobTitle: dto.jobTitle ?? null,
    department: dto.department ?? null,
    phone: dto.phone ?? null,
    timeZone: dto.timeZone,
    language: dto.language,
    country: dto.country ?? null,
    managerId: dto.managerId ?? null,
    version: dto.version,
  };
}

export function buildDetailQueryKey(
  orgSlug: string | null,
  userId: string | undefined,
): readonly unknown[] {
  return ['identity', 'users', orgSlug, 'detail', userId];
}

export function useOrgMemberDetail(
  userId: string | undefined,
): UseOrgMemberDetailResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const enabled =
    orgSlug !== null && typeof userId === 'string' && userId.length > 0;

  const query = useQuery({
    queryKey: buildDetailQueryKey(orgSlug, userId),
    enabled,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/users/{userId}',
        {
          params: {
            path: { slug: orgSlug as string, userId: userId as string },
          },
        },
      );
      if (result.response.status === 403) return FORBIDDEN;
      if (result.response.status === 404) return NOT_FOUND;
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/users/${userId} failed with status ${result.response.status}`,
        );
      }
      const dto = result.data as DetailDto;
      const projected = projectDetail(dto);
      const headerEtag = result.response.headers.get('etag');
      const etag =
        headerEtag !== null && headerEtag.length > 0
          ? headerEtag
          : `"${dto.version}"`;
      const envelope: ReadyEnvelope = { data: projected, etag };
      return envelope;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (!enabled || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  if (query.data === NOT_FOUND) {
    return { status: 'notFound', refetch };
  }
  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data,
    etag: envelope.etag,
    refetch,
  };
}
